import { Head } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import {
  applyTargets,
  createSeeds,
  decayWind,
  detachedRatio,
  detachSeeds,
  disperseAll,
  growWind,
  makeRng,
  retargetSeeds,
  stepSeeds,
  type DandelionConfig,
  type Seed,
  type WindState,
} from './dandelion';
import { sampleText, sampleTwoTone } from './murmuration';

/**
 * TASK-069 — soffier.emovia.com Easter Egg.
 *
 * A giant dandelion fill-page. Hold to blow seeds off the capolino; when
 * ≥85% are released the pun moment fires: seeds converge to spell
 * "SOFFIEREMO VIA" → morph → "SOFFIER.EMOVIA" (EMOVIA in brand colour) →
 * disperse. Then the question + CTA appear.
 *
 * SSR-safe: first render has no canvas, no random, no window.
 * Canvas/physics start only after mount (useEffect).
 */

// --------------------------------------------------------------------------
// Constants
// --------------------------------------------------------------------------

const SEED_COUNT = 110;
const PUN_THRESHOLD = 0.85; // ≥85% detached triggers pun
const FALLBACK_IDLE_MS = 25000; // auto-trigger after first blow (ms)

// Phase durations (seconds)
const HOLD1_DURATION = 2.5; // hold SOFFIEREMO VIA
const HOLD2_DURATION = 3.0; // hold SOFFIER.EMOVIA

// Brand colour (Emovia blue)
const BRAND_COLOR = '#6ea8ff';

// Hint text
const HINT_TEXT = 'Tieni premuto. Soffia.';

// Typewriter speed (ms per char)
const TYPER_MS = 55;

// Draw constants
const ACHENIO_R = 2.2;
const PAPPO_RAYS = 10;
const PAPPO_LEN = 9;
const SEED_COLOR_MAIN = 'rgba(245,235,210,0.85)';

// Dust motes: fixed set derived from index, no random per frame.
const DUST = Array.from({ length: 18 }, (_, i) => {
  const k = ((i * 1664525 + 1013904223) >>> 0) / 0xffffffff;
  const k2 = (((i * 1664525 + 22695477) >>> 0) / 0xffffffff + 0.1) % 1;
  return { xRatio: k, yRatio: k2, phase: k * Math.PI * 2, size: 1 + k * 2 };
});

// --------------------------------------------------------------------------
// Module-level drawing helpers (no React context needed)
// --------------------------------------------------------------------------

/**
 * Very cheap RGBA → blend without parsing.
 * `a` is like 'rgba(245,235,210,0.85)', `b` is hex like '#6ea8ff'.
 * Returns an rgb string interpolated by `t`.
 */
function blendColor(a: string, b: string, t: number): string {
  const mA = a.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!mA) return b;
  const rA = parseInt(mA[1]);
  const gA = parseInt(mA[2]);
  const bVal = parseInt(mA[3]);
  const mB = b.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!mB) return b;
  const rB = parseInt(mB[1], 16);
  const gB = parseInt(mB[2], 16);
  const bB = parseInt(mB[3], 16);
  return `rgb(${Math.round(rA + (rB - rA) * t)},${Math.round(gA + (gB - gA) * t)},${Math.round(bVal + (bB - bVal) * t)})`;
}

/** Draw a single seed (achenio + pappo rays) onto ctx. */
function drawSeed(
  ctx: CanvasRenderingContext2D,
  seed: Seed,
  tintColor: string,
): void {
  const { x, y, rot, alpha, tint } = seed;
  if (alpha <= 0) return;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(rot);

  const color = tint > 0.05 ? blendColor(SEED_COLOR_MAIN, tintColor, tint) : SEED_COLOR_MAIN;

  ctx.strokeStyle = color;
  ctx.lineWidth = 0.7;
  ctx.lineCap = 'round';
  for (let r = 0; r < PAPPO_RAYS; r++) {
    const a = (r / PAPPO_RAYS) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(a) * PAPPO_LEN, Math.sin(a) * PAPPO_LEN);
    ctx.stroke();
  }

  ctx.fillStyle = 'rgba(60,40,20,0.9)';
  ctx.beginPath();
  ctx.arc(0, 0, ACHENIO_R, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawCapolino(ctx: CanvasRenderingContext2D, seeds: Seed[], tintColor: string): void {
  for (const seed of seeds) {
    if (seed.status === 'attached') drawSeed(ctx, seed, tintColor);
  }
}

function drawAirborne(ctx: CanvasRenderingContext2D, seeds: Seed[], tintColor: string): void {
  for (const seed of seeds) {
    if (seed.status === 'airborne' || seed.status === 'forming') drawSeed(ctx, seed, tintColor);
  }
}

function drawScene(
  ctx: CanvasRenderingContext2D,
  seeds: Seed[],
  totalTime: number,
): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  ctx.clearRect(0, 0, w, h);

  // Golden-hour gradient background.
  const bg = ctx.createLinearGradient(0, 0, 0, h);
  bg.addColorStop(0, '#b8cfe0');
  bg.addColorStop(0.35, '#d4a96a');
  bg.addColorStop(0.7, '#e8c47a');
  bg.addColorStop(1, '#c9883a');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  // Dust motes.
  for (const d of DUST) {
    const x = d.xRatio * w + Math.sin(totalTime * 0.12 + d.phase) * 18;
    const y = d.yRatio * h + Math.sin(totalTime * 0.09 + d.phase * 1.3) * 12;
    ctx.beginPath();
    ctx.arc(x, y, d.size, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,235,180,${0.12 + Math.abs(Math.sin(totalTime * 0.3 + d.phase)) * 0.18})`;
    ctx.fill();
  }

  drawCapolino(ctx, seeds, BRAND_COLOR);
  drawAirborne(ctx, seeds, BRAND_COLOR);

  // Vignette overlay.
  const vig = ctx.createRadialGradient(w / 2, h / 2, h * 0.25, w / 2, h / 2, h * 0.85);
  vig.addColorStop(0, 'transparent');
  vig.addColorStop(1, 'rgba(20,10,5,0.55)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, w, h);
}

/** Build cached text samples for the pun moment. */
function ensureSamples(
  canvas: HTMLCanvasElement,
  offscreen: HTMLCanvasElement,
  samplesRef: React.MutableRefObject<{
    phase1: Float32Array;
    phase2: Float32Array;
    phase2Mask: Uint8Array;
  } | null>,
): void {
  if (samplesRef.current) return;
  const offCtx = offscreen.getContext('2d');
  if (!offCtx) return;

  offscreen.width = canvas.width;
  offscreen.height = canvas.height;

  const fontPx = Math.max(32, Math.round(canvas.width * 0.072));
  const step = Math.max(4, Math.round(fontPx / 7));

  const lines1 = canvas.width < 520 ? ['SOFFIEREMO', 'VIA'] : ['SOFFIEREMO VIA'];
  const phase1 = sampleText(
    offCtx,
    lines1,
    { width: canvas.width, height: canvas.height },
    fontPx,
    step,
  );

  // headLines is the same for both breakpoints; keep the variable for clarity.
  const headLines = ['SOFFIER.'];
  const tailLines = ['EMOVIA'];
  const { targets: phase2, tailMask: phase2Mask } = sampleTwoTone(
    offCtx,
    headLines,
    tailLines,
    { width: canvas.width, height: canvas.height },
    fontPx,
    step,
  );

  samplesRef.current = { phase1, phase2, phase2Mask };
}

// --------------------------------------------------------------------------
// Hooks
// --------------------------------------------------------------------------

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** Typewriter hook — drives a string char by char. */
function useTypewriter(text: string, active: boolean, msPerChar = TYPER_MS): string {
  const [typed, setTyped] = useState('');
  const idRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!active) return;
    let i = 0;
    setTyped('');
    idRef.current = setInterval(() => {
      i += 1;
      setTyped(text.slice(0, i));
      if (i >= text.length && idRef.current) {
        clearInterval(idRef.current);
        idRef.current = null;
      }
    }, msPerChar);
    return () => {
      if (idRef.current) clearInterval(idRef.current);
    };
  }, [active, text, msPerChar]);

  return typed;
}

// --------------------------------------------------------------------------
// Main component
// --------------------------------------------------------------------------

const Soffier = () => {
  const reducedMotion = useReducedMotion();

  // Refs that live outside React state — mutated directly in the rAF loop.
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const seedsRef = useRef<Seed[]>([]);
  const windRef = useRef<WindState>({ dx: -1, dy: 0, strength: 0 });
  const phaseRef = useRef<string>('idle');
  const phaseAgeRef = useRef(0);
  const totalTimeRef = useRef(0);
  const rafRef = useRef<number>(0);
  const lastTsRef = useRef<number>(-1);
  const firstBlowTsRef = useRef<number>(-1);
  const blowingRef = useRef(false);
  const punDoneRef = useRef(false);
  const samplesRef = useRef<{
    phase1: Float32Array;
    phase2: Float32Array;
    phase2Mask: Uint8Array;
  } | null>(null);
  // Stable ref to setPunComplete so the rAF loop can call it without being a dep.
  const setPunCompleteRef = useRef<(v: boolean) => void>(() => undefined);

  const [punComplete, setPunComplete] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hintGone, setHintGone] = useState(false);
  const [showCta, setShowCta] = useState(false);

  // Keep the ref pointing to the latest setter so the rAF loop can call it
  // without being listed as a dep of the main effect. No deps = runs every
  // render, which is correct — we always want the freshest reference.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPunCompleteRef.current = setPunComplete; });

  const question = 'Tu, cosa soffieresti via?';
  const typedQuestion = useTypewriter(question, punComplete);

  useEffect(() => {
    if (typedQuestion.length >= question.length && punComplete) setShowCta(true);
  }, [typedQuestion, punComplete]);

  useEffect(() => {
    if (reducedMotion) return;
    const id = setTimeout(() => setShowHint(true), 2000);
    return () => clearTimeout(id);
  }, [reducedMotion]);

  // -------------------------------------------------------------------------
  // Main rAF loop effect. All helpers are local to avoid dep warnings.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    // Alias as non-nullable for the entire effect closure.
    const canvas: HTMLCanvasElement = canvasEl;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Offscreen canvas for text sampling.
    const offscreen = document.createElement('canvas');
    offscreen.width = window.innerWidth;
    offscreen.height = window.innerHeight;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const cfg: DandelionConfig = {
        count: SEED_COUNT,
        cx: Math.round(canvas.width * 0.58),
        cy: Math.round(canvas.height * 0.44),
        radius: Math.min(canvas.width, canvas.height) * 0.18,
      };
      seedsRef.current = createSeeds(cfg);
      samplesRef.current = null;
    };

    const ro = new ResizeObserver(resize);
    ro.observe(document.documentElement);
    resize();

    // --- Inner phase helpers (local to effect for clean closure) ---

    function triggerPun() {
      if (punDoneRef.current) return;
      const seeds = seedsRef.current;
      const wind = windRef.current;
      wind.strength = Math.max(wind.strength, 0.5);
      for (const s of seeds) {
        if (s.status === 'attached') {
          s.status = 'airborne';
          s.stateAge = 0;
          const rng = makeRng(s.noiseSeed * 17);
          s.vx = (rng() - 0.5) * 4;
          s.vy = -rng() * 3 - 1;
          s.rotSpeed = (rng() - 0.5) * 0.2;
          s.alpha = 1;
        }
      }
      ensureSamples(canvas, offscreen, samplesRef);
      const samples = samplesRef.current;
      if (samples) {
        applyTargets(seeds, samples.phase1, { width: canvas.width, height: canvas.height });
      }
      phaseRef.current = 'pun-forming';
      phaseAgeRef.current = 0;
    }

    function updatePhase(dt: number) {
      const seeds = seedsRef.current;
      const wind = windRef.current;
      const samples = samplesRef.current;

      switch (phaseRef.current) {
        case 'pun-forming':
        case 'pun-sliding': {
          const forming = seeds.filter((s) => s.status === 'forming');
          const settled = forming.filter((s) => {
            const ext = s as Seed & { targetX?: number; targetY?: number };
            return ext.targetX !== undefined && Math.hypot(s.x - ext.targetX, s.y - (ext.targetY ?? 0)) < 8;
          });
          const settleRatio = forming.length > 0 ? settled.length / forming.length : 0;
          if (settleRatio >= 0.9 || phaseAgeRef.current >= 4) {
            if (phaseRef.current === 'pun-forming') {
              phaseRef.current = 'pun-hold1';
            } else {
              phaseRef.current = 'pun-hold2';
            }
            phaseAgeRef.current = 0;
          }
          break;
        }
        case 'pun-hold1': {
          if (phaseAgeRef.current >= HOLD1_DURATION) {
            if (samples) retargetSeeds(seeds, samples.phase2, samples.phase2Mask);
            phaseRef.current = 'pun-sliding';
            phaseAgeRef.current = 0;
          }
          break;
        }
        case 'pun-hold2': {
          if (phaseAgeRef.current >= HOLD2_DURATION) {
            disperseAll(seeds, makeRng(99));
            phaseRef.current = 'pun-dispersing';
            phaseAgeRef.current = 0;
            wind.strength = 0.5;
            wind.dx = 1;
            wind.dy = -0.3;
          }
          break;
        }
        case 'pun-dispersing': {
          const anyVisible = seeds.some((s) => s.status !== 'gone' && s.status !== 'attached');
          if (!anyVisible || phaseAgeRef.current >= 3) {
            phaseRef.current = 'done';
            punDoneRef.current = true;
            setPunCompleteRef.current(true);
          }
          break;
        }
        default:
          break;
      }

      void dt;
    }

    // --- rAF loop ---
    let stopped = false;

    const loop = (ts: number) => {
      if (stopped) return;
      rafRef.current = requestAnimationFrame(loop);

      if (lastTsRef.current < 0) {
        lastTsRef.current = ts;
        return;
      }
      const dt = Math.min((ts - lastTsRef.current) / 1000, 0.05);
      lastTsRef.current = ts;
      totalTimeRef.current += dt;

      const seeds = seedsRef.current;
      const wind = windRef.current;

      // Wind.
      if (blowingRef.current) {
        growWind(wind, dt);
        if (firstBlowTsRef.current < 0) firstBlowTsRef.current = totalTimeRef.current;
      } else {
        decayWind(wind, dt);
      }

      // Detach on blow (only in idle phase).
      if (wind.strength > 0.05 && phaseRef.current === 'idle') {
        detachSeeds(seeds, wind);
      }

      // Pun trigger.
      if (!punDoneRef.current && phaseRef.current === 'idle' && firstBlowTsRef.current > 0) {
        const elapsed = totalTimeRef.current - firstBlowTsRef.current;
        const ratio = detachedRatio(seeds);
        if (ratio >= PUN_THRESHOLD || elapsed >= FALLBACK_IDLE_MS / 1000) {
          triggerPun();
        }
      }

      // Phase state machine.
      if (phaseRef.current !== 'idle' && phaseRef.current !== 'done') {
        phaseAgeRef.current += dt;
        updatePhase(dt);
      }

      stepSeeds(seeds, wind, { width: canvas.width, height: canvas.height }, dt, totalTimeRef.current);
      drawScene(ctx, seeds, totalTimeRef.current);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      stopped = true;
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [reducedMotion]);

  // -------------------------------------------------------------------------
  // Input handlers
  // -------------------------------------------------------------------------

  const getWindDir = (e: React.PointerEvent<HTMLCanvasElement>): { dx: number; dy: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { dx: -1, dy: 0 };
    const seeds = seedsRef.current;
    if (seeds.length === 0) return { dx: -1, dy: 0 };
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const cx = seeds.reduce((s, sd) => s + sd.x, 0) / seeds.length;
    const cy = seeds.reduce((s, sd) => s + sd.y, 0) / seeds.length;
    const dx = cx - px;
    const dy = cy - py;
    const len = Math.hypot(dx, dy) || 1;
    return { dx: dx / len, dy: dy / len };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const dir = getWindDir(e);
    windRef.current.dx = dir.dx;
    windRef.current.dy = dir.dy;
    blowingRef.current = true;
    if (!hintGone) setHintGone(true);
  };

  const onPointerUp = () => {
    blowingRef.current = false;
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.key !== 'Enter') return;
      e.preventDefault();
      windRef.current.dx = -1;
      windRef.current.dy = 0;
      blowingRef.current = true;
      setHintGone(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.key !== 'Enter') return;
      blowingRef.current = false;
    };
    const onVisibility = () => {
      if (document.hidden) {
        blowingRef.current = false;
        windRef.current.strength = 0;
        lastTsRef.current = -1;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Reduced-motion static render
  // -------------------------------------------------------------------------
  if (reducedMotion) {
    return (
      <div className="soffier-root">
        <Head title="soffieremovia" />
        <h1 className="sr-only">soffieremovia</h1>
        <div className="soffier-reduced-bg" aria-hidden="true" />
        <main className="soffier-done-stage">
          <p className="soffier-pun-title" aria-hidden="true">
            <span>SOFFIER.</span>
            <span className="soffier-brand">EMOVIA</span>
          </p>
          <p
            className="soffier-question"
            aria-label="Tu, cosa soffieresti via?"
            role="heading"
            aria-level={2}
          >
            Tu, cosa soffieresti via?
          </p>
          <a className="soffier-cta soffier-cta-on" href="https://www.emovia.com">
            Scopri Emovia
            <span aria-hidden="true" className="soffier-cta-arrow">
              →
            </span>
          </a>
        </main>
        <style>{STYLES}</style>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Normal render
  // -------------------------------------------------------------------------
  return (
    <div className="soffier-root">
      <Head title="soffieremovia" />

      {/* Accessible anchors in the markup for SSR / no-JS fallback. */}
      <h1 className="sr-only">soffieremovia</h1>
      <p className="sr-only" aria-hidden="true">
        Tu, cosa soffieresti via?
      </p>
      <a
        className="soffier-skip"
        href="https://www.emovia.com"
        tabIndex={punComplete ? -1 : 0}
      >
        Scopri Emovia
      </a>

      {/* Stelo — SVG behind canvas */}
      <svg
        className="soffier-stelo"
        aria-hidden="true"
        viewBox="0 0 80 400"
        preserveAspectRatio="none"
      >
        <path
          className="soffier-stelo-path"
          d="M40 400 C35 300 55 250 42 180 C38 130 44 80 40 0"
          stroke="#6a8a3a"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
      </svg>

      {/* Main canvas */}
      <canvas
        ref={canvasRef}
        className="soffier-canvas"
        aria-hidden="true"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />

      {/* Hint */}
      {!hintGone && (
        <p
          className={`soffier-hint${showHint ? ' soffier-hint-on' : ''}`}
          aria-hidden="true"
        >
          {HINT_TEXT}
        </p>
      )}

      {/* Pun done → question + CTA */}
      {punComplete && (
        <main className="soffier-done-stage" aria-live="polite">
          <p className="soffier-question" role="heading" aria-level={2}>
            {typedQuestion}
            {typedQuestion.length < question.length && (
              <span className="soffier-caret" aria-hidden="true">
                |
              </span>
            )}
          </p>
          <a
            className={`soffier-cta${showCta ? ' soffier-cta-on' : ''}`}
            href="https://www.emovia.com"
          >
            Scopri Emovia
            <span aria-hidden="true" className="soffier-cta-arrow">
              →
            </span>
          </a>
        </main>
      )}

      <style>{STYLES}</style>
    </div>
  );
};

// --------------------------------------------------------------------------
// Styles
// --------------------------------------------------------------------------

const STYLES = `
.soffier-root {
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: linear-gradient(to bottom, #b8cfe0 0%, #d4a96a 40%, #e8c47a 70%, #c9883a 100%);
  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
}

.soffier-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: default;
  touch-action: none;
}

.soffier-stelo {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: clamp(30px, 8vw, 60px);
  height: clamp(140px, 35vh, 280px);
  pointer-events: none;
  z-index: 1;
  opacity: 0.7;
  animation: soffier-sway 6s ease-in-out infinite;
}
@keyframes soffier-sway {
  0%, 100% { transform: translateX(-50%) rotate(-1.5deg); }
  50%       { transform: translateX(-50%) rotate(1.5deg); }
}

.soffier-reduced-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, #b8cfe0 0%, #d4a96a 40%, #e8c47a 70%, #c9883a 100%);
}

/* Hint */
.soffier-hint {
  position: absolute;
  bottom: clamp(1.5rem, 5vh, 3rem);
  left: 50%;
  transform: translateX(-50%);
  font-size: clamp(0.85rem, 2.5vw, 1.1rem);
  letter-spacing: 0.08em;
  color: rgba(255,248,220,0.0);
  pointer-events: none;
  transition: color 0.8s ease;
  white-space: nowrap;
  z-index: 10;
  text-shadow: 0 1px 4px rgba(80,40,0,0.4);
}
.soffier-hint-on {
  color: rgba(255,248,220,0.75);
}

/* Pun done overlay */
.soffier-done-stage {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(1.2rem, 4vh, 2.5rem);
  padding: 0 clamp(1rem, 5vw, 3rem);
  text-align: center;
  pointer-events: none;
  z-index: 20;
  animation: soffier-fade-in 0.8s ease both;
}

/* Static reduced-motion title */
.soffier-pun-title {
  font-size: clamp(1.8rem, 8vw, 5rem);
  font-weight: 800;
  letter-spacing: 0.05em;
  color: rgba(255,248,210,0.9);
  text-shadow: 0 2px 18px rgba(80,40,0,0.35);
  line-height: 1.1;
}
.soffier-brand {
  color: ${BRAND_COLOR};
  text-shadow: 0 0 18px rgba(110,168,255,0.45);
}

.soffier-question {
  font-size: clamp(1rem, 3.5vw, 1.75rem);
  font-weight: 500;
  letter-spacing: 0.03em;
  color: rgba(255,248,220,0.9);
  min-height: 1.4em;
  text-shadow: 0 1px 8px rgba(80,40,0,0.3);
}
.soffier-caret {
  animation: soffier-blink 0.9s steps(2, start) infinite;
}

.soffier-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: clamp(0.75rem, 1.5vw, 1rem) clamp(1.5rem, 3vw, 2.5rem);
  border-radius: 999px;
  font-size: clamp(1rem, 2.8vw, 1.35rem);
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #fff;
  background: hsl(225 100% 40%);
  box-shadow: 0 8px 30px hsl(225 100% 40% / 0.45);
  text-decoration: none;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.5s ease, transform 0.5s ease, box-shadow 0.25s ease;
  pointer-events: none;
  z-index: 30;
}
.soffier-cta-on {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}
.soffier-cta:hover {
  box-shadow: 0 10px 38px hsl(225 100% 40% / 0.6);
}
.soffier-cta:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0,0,0,0.15), 0 0 0 6px ${BRAND_COLOR};
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}
.soffier-cta-arrow {
  transition: transform 0.2s ease;
}
.soffier-cta:hover .soffier-cta-arrow {
  transform: translateX(4px);
}

/* Skip link (keyboard / SSR fallback) */
.soffier-skip {
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 100;
}
.soffier-skip:focus-visible {
  position: static;
  color: rgba(255,248,220,0.9);
  text-decoration: underline;
}

/* Keyframes */
@keyframes soffier-fade-in {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes soffier-blink {
  50% { opacity: 0; }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 380px) {
  .soffier-pun-title {
    font-size: clamp(1.5rem, 9vw, 2.2rem);
  }
  .soffier-question {
    font-size: clamp(0.9rem, 4vw, 1.2rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .soffier-stelo,
  .soffier-caret,
  .soffier-done-stage {
    animation: none;
  }
  .soffier-cta {
    opacity: 1;
    transform: none;
    pointer-events: auto;
  }
}
`;

export default Soffier;
